import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import DefaultPopup from '@r1-frontend/ui-react/components/popups/defaultPopup';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import { FullWidthListContainer, IndentContainer, ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import connectPopupAnalytics from '~/src/components/ConnectDomruPopup/analytics';
import { CLIENT_REFERRAL_LINK } from '~/src/components/ConnectDomruPopup/constants';
import { setView } from '~/src/components/ConnectDomruPopup/context/actions';
import { useContextDispatch } from '~/src/components/ConnectDomruPopup/context/hooks';
import { connectionWithStopper } from '~/src/components/ConnectDomruPopup/helpers';
import { isLeadGenerationHidden } from '~/src/helpers/isLeadGenerationHidden';
import { selectCompanyName } from '~/src/store/selectors/city';

import * as ST from './styled';

const SelectConnectionMethod = () => {
    const router = useRouter();

    const isDesktop = useMatchMediaByWidth(wideBreakpoints.mobile, true);

    const companyName = useSelector(selectCompanyName);

    const dispatch = useContextDispatch();

    const [isLoading, setIsLoading] = useState(false);

    const hideLeadGeneration = useMemo(isLeadGenerationHidden, []);

    const isConnectionWithStopper = useMemo(connectionWithStopper, []);

    const { title, subtitle, description, bgPath } = useMemo(() => {
        return {
            title: isConnectionWithStopper
                ? 'Оформить за&nbsp;2&nbsp;минуты'
                : `Подключить ${companyName}`,
            subtitle: isConnectionWithStopper
                ? 'Выберите тариф и&nbsp;оформите заявку онлайн за&nbsp;пару минут.'
                : 'Оформите заявку онлайн за&nbsp;пару минут',
            description: isConnectionWithStopper
                ? 'Или оставьте свой номер телефона, и&nbsp;менеджер свяжется с&nbsp;вами в&nbsp;течение дня.'
                : 'Сложно определиться? Можем позвонить и&nbsp;помочь вам выбрать подходящий тариф!',
            bgPath: isConnectionWithStopper
                ? '/static/images/connect-domru/bg-abn.desktop.png'
                : '/static/images/connect-domru/bg.desktop.png',
        };
    }, [isConnectionWithStopper]);

    const handleClose = () => {
        dispatch(setView('none'));
    };

    const onClickReferralButton = useCallback(() => {
        if (location.pathname === CLIENT_REFERRAL_LINK) {
            handleClose();
        } else {
            setIsLoading(true);
            connectPopupAnalytics.applicationForm.move();
            router.push(CLIENT_REFERRAL_LINK).finally(() => setIsLoading(false));
        }
    }, []);

    const onShowRequestFormPopup = useCallback(() => {
        connectPopupAnalytics.applicationFormHeader.move();
        dispatch(setView(isConnectionWithStopper ? 'stopper' : 'callback'));
    }, [isConnectionWithStopper]);

    return (
        <DefaultPopup
            isOpen
            onClose={handleClose}
            width={isDesktop ? '600px' : '308px'}
            height={'auto'}
            fullWidth={true}
            data-test='become-client-popup'
        >
            <FullWidthListContainer $size={0}>
                <ST.TopBlock $desktop={[32, 32, 24]} $mobile={[24]} id={'topBlock'} bgPath={bgPath}>
                    <ListContainer
                        $direction={'column'}
                        $alignItems={isDesktop ? 'flex-start' : 'unset'}
                        $size={36}
                    >
                        <TextWrapper $lastIndent={false}>
                            <H3 dangerouslySetInnerHTML={{ __html: title }}/>
                            <Paragraph4 dangerouslySetInnerHTML={{ __html: subtitle }}/>
                        </TextWrapper>
                        <Button
                            bType={'action'}
                            loading={isLoading}
                            disabled={isLoading}
                            onClick={onClickReferralButton}
                            data-test='one-rouble-link'
                        >
                            Выбрать тариф
                        </Button>
                    </ListContainer>
                </ST.TopBlock>

                {!hideLeadGeneration && (
                    <IndentContainer $desktop={[24, 32, 32]} $mobile={[24, 32]} id={'bottomBlock'}>
                        <FullWidthListContainer
                            $direction={'column'}
                            $alignItems={isDesktop ? 'flex-start' : 'unset'}
                            $size={16}
                        >
                            <Paragraph4 dangerouslySetInnerHTML={{ __html: description }}/>
                            <Button
                                bType={'secondary'}
                                loading={isLoading}
                                disabled={isLoading}
                                onClick={onShowRequestFormPopup}
                            >
                                Позвоните мне
                            </Button>
                        </FullWidthListContainer>
                    </IndentContainer>
                )}
            </FullWidthListContainer>
        </DefaultPopup>
    );
};

export default SelectConnectionMethod;
